/**
 * The ONE definition of a provider's status chips. Rendered identically on the list and the
 * details page so the two pages speak one vocabulary.
 *
 * Tone maps to meaning, consistently:
 *   grey  — inert, nothing to do
 *   amber — needs attention, yours or theirs
 *   green — live
 *   red   — stopped
 */
export function providerStatusChips(provider) {
  const chips = [];

  if (provider.providerState === 'SUBMITTED') {
    chips.push({ label: 'Submitted', tone: 'amber' });
  } else if (provider.providerState === 'DECLINED') {
    chips.push({ label: 'Declined', tone: 'red' });
  }

  chips.push(
    provider.user
      ? { label: 'Claimed', tone: 'green' }
      : { label: 'Unclaimed', tone: 'grey' },
  );

  // Suspended REPLACES the visibility chip. suspendProvider forces visible = false, so showing
  // both would imply two independent states where there is one.
  if (provider.adminSuspended) {
    chips.push({ label: 'Suspended', tone: 'red' });
  } else if (provider.visible === false) {
    chips.push({ label: 'Hidden', tone: 'grey' });
  } else {
    // `!== false`, NOT `=== true`: ProviderEligibility.isListable treats a legacy NULL as
    // visible, and the admin panel must not disagree with public search.
    chips.push({ label: 'Visible', tone: 'green' });
  }

  // The two states the green chips cannot express.
  //
  // Tests CONSENT specifically rather than `listable === false`. isListable also fails for
  // non-APPROVED, hidden, suspended and unclaimed providers -- all four of which already have
  // their own chip above, so keying off it put six chips on a hidden unclaimed seeded row and
  // said nothing new. Consent is the one cause nothing else can show. This is the same reasoning
  // ProviderSortKeys.statusRank uses for rank 1, so chip and sort band now agree.
  if (!provider.user && provider.consentStatus && provider.consentStatus !== 'GRANTED') {
    chips.push({
      label: 'Not listed',
      tone: 'amber',
      title: `Unclaimed and consent is ${provider.consentStatus} — invisible to public search until consent is GRANTED`,
    });
  }
  // SERVICE only. ProPlanGuard documents STORE and INFLUENCER as outside the tier/service
  // model, so they legitimately have no priced services -- flagging every influencer row amber
  // for "not bookable" would be permanent noise. Legacy null counts as SERVICE, matching
  // ProPlanGuard.effectiveType.
  const isServiceProvider = (provider.providerType || 'SERVICE') === 'SERVICE';
  if (isServiceProvider && !(provider.services || []).some((s) => Number(s.price) > 0)) {
    chips.push({
      label: 'No priced service',
      tone: 'amber',
      title: 'Listed but not bookable — needs at least one service priced above 0',
    });
  }

  chips.push(
    provider.isProPlan
      ? { label: 'PRO', tone: 'green' }
      : { label: 'FREE', tone: 'grey' },
  );

  return chips;
}
